import { gql, useMutation, useQuery } from '@apollo/client'
import { useEffect } from 'react'

import { currentArtistVar } from '@/clients/utoniq-core'
import {
  ArtistBottomTabFragmentDoc,
  ArtistLiveStreamQuery,
  ArtistLiveStreamQueryVariables,
  ArtistThreadChannelConnectionFragmentDoc,
  ArtistThreadChannelOrderByType,
  ArtistTopForGuestQuery,
  ArtistTopForGuestQueryVariables,
  ArtistTopQuery,
  ArtistTopQueryVariables,
  CurrentUserFragmentDoc,
  EventsByArtistConnectionFragmentDoc,
  FollowerFragmentDoc,
  FollowMutation,
  FollowMutationVariables,
  GachaBoxFragmentDoc,
  GachasConnectionFragmentDoc,
  LikePost,
  LikePostMutation,
  LikePostMutationVariables,
  MissionBoxFragmentDoc,
  MissionsConnectionFragmentDoc,
  MissionType,
  NewStoreItemConnectionFragmentDoc,
  OrganizationNotificationConnectionFragmentDoc,
  PostBoxFragmentDoc,
  RankingBaseConnectionFragmentDoc,
  RankingStatus,
  RankingUserWithoutIdConnectionFragmentDoc,
  UnfollowMutation,
  UnfollowMutationVariables,
  UnlikePost,
  UnlikePostMutation,
  UnlikePostMutationVariables,
  UserStepMissionsConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import {
  useAuth,
  useCurrentUser,
  useRouter,
  useTrackPageView,
  useTranslation,
} from '@/core'
import { useMessage } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { Share } from '@/core/native'
import { setRedirectAfterLoginPath } from '@/core/native/storage'
import { useCreateShortLink } from '@/lib/url/shortLink'

export const useViewModel = makeViewModel(() => {
  const { t } = useTranslation('artistTop')
  const { user, userClaims } = useCurrentUser()
  const { router, location } = useRouter<{ artistId: string }>()
  const { reloadUser } = useAuth()
  const { showError } = useMessage()
  const artistId = router.query.artistId ?? config.artistId

  useTrackPageView(() => {
    /**
     * HACK: delay for save
     */
    setTimeout(() => setRedirectAfterLoginPath(location.pathname), 300)
  })

  const { data, loading } = useQuery<ArtistTopQuery, ArtistTopQueryVariables>(
    ARTIST_TOP,
    {
      skip: !user?.isSigned,
      variables: {
        artistId,
        artistIdString: artistId,
        rankingArtistId: artistId,
        missionsTypeFilter: [MissionType.Quiz, MissionType.ItemCollect],
        artistThreadChannelsInput: {
          artistId,
          options: { orderBy: ArtistThreadChannelOrderByType.CreatedAt },
        },
        filters: {
          isPublished: true,
          reviewStatus: 'accepted',
          salesStatus: 'active',
        },
      },
      onCompleted: data => {
        if (!data?.artist) return
        currentArtistVar(data.artist)
      },
    }
  )
  const isNextFetch = !!data

  const { data: dataForGuest, loading: loadingForGuest } = useQuery<
    ArtistTopForGuestQuery,
    ArtistTopForGuestQueryVariables
  >(ARTIST_TOP_FOR_GUEST, {
    skip: !!user?.isSigned,
    variables: {
      artistId,
      artistIdString: artistId,
      missionsTypeFilter: [MissionType.Quiz, MissionType.ItemCollect],
      artistThreadChannelsInput: {
        artistId,
        options: { orderBy: ArtistThreadChannelOrderByType.CreatedAt },
      },
      filters: {
        isPublished: true,
        reviewStatus: 'accepted',
        salesStatus: 'active',
      },
    },
    onCompleted: data => {
      if (!data?.artist) return
      currentArtistVar(data.artist)
    },
  })
  const isNextFetchForGuest = !!dataForGuest

  /**
   * @description
   * ライブ配信開始・終了検知のため10秒毎にcurrentLiveのみpollingする
   */
  const { data: dataLiveStream } = useQuery<
    ArtistLiveStreamQuery,
    ArtistLiveStreamQueryVariables
  >(ARTIST_LIVE_STREAM, {
    variables: { artistId },
    pollInterval: 10000,
    fetchPolicy: 'network-only',
    skip: !config.modules.liveStream,
  })

  const [follow] = useMutation<FollowMutation, FollowMutationVariables>(FOLLOW)
  const [unfollow] = useMutation<UnfollowMutation, UnfollowMutationVariables>(
    UNFOLLOW
  )

  const [likePost] = useMutation<LikePostMutation, LikePostMutationVariables>(
    LikePost
  )

  const [unlikePost] = useMutation<
    UnlikePostMutation,
    UnlikePostMutationVariables
  >(UnlikePost)

  const getShortLinkUrl = useCreateShortLink({
    path:
      config.applicationType === 'standalone'
        ? '/home'
        : `/artists/${artistId}/home`,
  })

  useEffect(() => {
    // スタンドアローンの場合、アーティストをフォローする
    if (
      !user ||
      config.applicationType !== 'standalone' ||
      !!data?.artist?.follower ||
      !config.artistId
    )
      return

    follow({
      variables: { artistId },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid])

  useEffect(() => {
    if (user && !loading && config.applicationType === 'standalone') {
      reloadUser().catch(showError)
    }
  }, [loading])

  return {
    state: {
      loading:
        (loading && !isNextFetch) || (loadingForGuest && !isNextFetchForGuest),
      artistId,
      artist: (data ?? dataForGuest)?.artist || null,
      newGachas: (data ?? dataForGuest)?.gachas?.edges.map(e => e.node) || [],
      newMissions:
        (data ?? dataForGuest)?.missionsWithFilteres?.edges.map(e => e.node) ||
        [],
      newPosts:
        (data ?? dataForGuest)?.artist?.posts?.edges.map(e => e.node) || [],
      membership: (data ?? dataForGuest)?.artist?.subscription || null,
      banners: (data ?? dataForGuest)?.artist?.banners || [],
      memberArtists: user ? data?.user?.organizationPatrons : null,
      followArtists: user ? data?.user?.organizationFollowees : null,
      user: data?.user ?? null,
      isArtist: !!userClaims?.orgs?.[config.organizationId]?.artist,
      liveStream: dataLiveStream?.artist?.currentLive ?? null,
      newThreads:
        (data ?? dataForGuest)?.artistThreadChannels?.edges.map(e => e.node) ||
        [],
      isBlocked: !!data?.artist?.isBlocked,
      myRanking:
        data?.artistRankings?.status === RankingStatus.Active
          ? data?.artistRankings?.latestRankingPeriod?.myRanking ?? null
          : null,
      hasMission:
        !!data?.missionsWithFilteres?.edges.length ||
        !!data?.artistStepMissions?.edges.length,
      events: data?.eventsByArtist ?? dataForGuest?.eventsByArtist ?? null,
      notifications:
        (
          data ?? dataForGuest
        )?.organizationOfficialArtistNotifications?.edges.slice(0, 3) ?? null,
    },
    actions: {
      async onFollow() {
        await follow({
          variables: { artistId },
          optimisticResponse: () => {
            return {
              follow: {
                __typename: 'Follower',
                id: `${data?.user?.id}:${data?.artist?.id}`,
                user: {
                  id: data?.user?.id ?? '',
                  __typename: 'User',
                },
                artist: {
                  id: artistId,
                  displayName: data?.artist?.displayName ?? '',
                  avatar: {
                    uri: data?.artist?.avatar?.smallUri ?? '',
                    __typename: 'Avatar',
                  },
                  follower: {
                    artistId: artistId,
                    id: `${data?.user?.id}:${data?.artist?.id}`,
                    __typename: 'Follower',
                  },
                  __typename: 'Artist',
                },
              },
            }
          },
        }).catch(showError)
      },
      async onUnfollow() {
        await unfollow({
          variables: { artistId },
          optimisticResponse: () => ({
            unfollow: {
              __typename: 'Follower',
              id: `${data?.user?.id}:${data?.artist?.id}`,
              user: {
                id: data?.user?.id ?? '',
                __typename: 'User',
              },
              artist: {
                id: artistId,
                displayName: data?.artist?.displayName ?? '',
                avatar: data?.artist?.avatar,
                follower: null,
                __typename: 'Artist',
              },
            },
          }),
        })
      },
      async onLikePost(postId: string, isLike: boolean, likeCount: number) {
        if (isLike) {
          await unlikePost({
            variables: { unlikePostPostId: postId },
            optimisticResponse: ({ unlikePostPostId }) => ({
              unlikePost: {
                __typename: 'Post',
                id: unlikePostPostId,
                isLike: false,
                likeCount: --likeCount,
              },
            }),
          })
          return
        }
        await likePost({
          variables: { likePostPostId: postId },
          optimisticResponse: ({ likePostPostId }) => ({
            likePost: {
              __typename: 'Post',
              id: likePostPostId,
              isLike: true,
              likeCount: ++likeCount,
            },
          }),
        })
      },
      async onClickShare() {
        const url = await getShortLinkUrl()
        await Share.share({
          title: `${data?.artist?.displayName} | ${t('metaTitle')}`,
          text: data?.artist?.profileText ?? `${t('metaTitle')}`,
          url,
          dialogTitle: `${data?.artist?.displayName} | ${t('metaTitle')}`,
        })
      },
      async onUnBlock() {
        /**
         * @todo
         * ブロック解除API本格実装後に実装
         * https://app.clickup.com/t/908127/UT-8682
         */
      },
    },
  }
})

const UNFOLLOW = gql`
  mutation unfollow($artistId: ID!) {
    unfollow(artistId: $artistId) {
      ...Follower
    }
  }
  ${FollowerFragmentDoc}
`

const FOLLOW = gql`
  mutation follow($artistId: ID!) {
    follow(artistId: $artistId) {
      ...Follower
    }
  }
  ${FollowerFragmentDoc}
`

const ARTIST_TOP = gql`
  query ArtistTop(
    $artistId: ID!
    $artistIdString: String!
    $rankingArtistId: String!
    $missionsTypeFilter: [MissionType!]
    $artistThreadChannelsInput: ArtistThreadChannelsInput!
    $filters: ItemsFilterInput
  ) {
    user {
      ...CurrentUser
    }
    artistRankings(artistId: $rankingArtistId) {
      ...RankingBaseConnection
      latestRankingPeriod(periodType: monthly) {
        myRanking {
          ...RankingUserWithoutIdConnection
        }
      }
    }
    artist(id: $artistId) {
      ...ArtistBottomTab
      id
      displayName
      accountId
      introduction
      formattedAccountId @client
      profileText
      textLink {
        content
        uri
      }
      avatar {
        uri
        mediumUri
        smallUri
      }
      follower {
        id
        artistId
      }
      subscription {
        products {
          plans {
            planId
            status
          }
          content
        }
      }
      sns {
        facebook
        instagram
        tiktok
        twitter
        youtube
      }
      banners {
        thumbnail {
          uri
          mediumUri
          smallUri
        }
        link {
          innerPath
          externalUrl
        }
      }
      posts {
        edges {
          node {
            ...PostBox
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
      patron {
        id
        isPersonalInformationRequired
        subscription {
          planId
        }
      }
      isBlocked
    }
    gachas(artistId: $artistId) {
      ...GachasConnection
    }
    missionsWithFilteres(artistId: $artistId, typeFilter: $missionsTypeFilter) {
      ...MissionsConnection
    }
    artistStepMissions(artistId: $artistId) {
      ...UserStepMissionsConnection
    }
    artistThreadChannels(inputs: $artistThreadChannelsInput) {
      ...ArtistThreadChannelConnection
    }
    items(artistId: $artistId, filters: $filters) {
      ...NewStoreItemConnection
    }
    eventsByArtist(artistId: $artistIdString) {
      ...EventsByArtistConnection
    }
    organizationOfficialArtistNotifications(
      artistId: $artistIdString
      pageInput: { limit: 3 }
    ) {
      ...OrganizationNotificationConnection
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${RankingBaseConnectionFragmentDoc}
  ${CurrentUserFragmentDoc}
  ${RankingUserWithoutIdConnectionFragmentDoc}
  ${GachaBoxFragmentDoc}
  ${MissionBoxFragmentDoc}
  ${PostBoxFragmentDoc}
  ${GachasConnectionFragmentDoc}
  ${MissionsConnectionFragmentDoc}
  ${ArtistThreadChannelConnectionFragmentDoc}
  ${UserStepMissionsConnectionFragmentDoc}
  ${NewStoreItemConnectionFragmentDoc}
  ${EventsByArtistConnectionFragmentDoc}
  ${OrganizationNotificationConnectionFragmentDoc}
`

const ARTIST_TOP_FOR_GUEST = gql`
  query ArtistTopForGuest(
    $artistId: ID!
    $artistIdString: String!
    $missionsTypeFilter: [MissionType!]
    $artistThreadChannelsInput: ArtistThreadChannelsInput!
    $filters: ItemsFilterInput
  ) {
    artist(id: $artistId) {
      ...ArtistBottomTab
      id
      displayName
      accountId
      formattedAccountId @client
      profileText
      textLink {
        content
        uri
      }
      introduction
      avatar {
        uri
        mediumUri
        smallUri
      }
      follower {
        id
        artistId
      }
      subscription {
        products {
          plans {
            planId
            status
          }
          content
        }
      }
      sns {
        facebook
        instagram
        tiktok
        twitter
        youtube
      }
      banners {
        thumbnail {
          uri
          mediumUri
          smallUri
        }
        link {
          innerPath
          externalUrl
        }
      }
      posts {
        edges {
          node {
            ...PostBox
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
      patron {
        id
      }
    }
    gachas(artistId: $artistId) {
      ...GachasConnection
    }
    missionsWithFilteres(artistId: $artistId, typeFilter: $missionsTypeFilter) {
      ...MissionsConnection
    }
    artistThreadChannels(inputs: $artistThreadChannelsInput) {
      ...ArtistThreadChannelConnection
    }
    items(artistId: $artistId, filters: $filters) {
      ...NewStoreItemConnection
    }
    eventsByArtist(artistId: $artistIdString) {
      ...EventsByArtistConnection
    }
    organizationOfficialArtistNotifications(
      artistId: $artistIdString
      pageInput: { limit: 3 }
    ) {
      ...OrganizationNotificationConnection
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${GachaBoxFragmentDoc}
  ${MissionBoxFragmentDoc}
  ${PostBoxFragmentDoc}
  ${GachasConnectionFragmentDoc}
  ${MissionsConnectionFragmentDoc}
  ${ArtistThreadChannelConnectionFragmentDoc}
  ${NewStoreItemConnectionFragmentDoc}
  ${EventsByArtistConnectionFragmentDoc}
  ${OrganizationNotificationConnectionFragmentDoc}
`

const ARTIST_LIVE_STREAM = gql`
  query ArtistLiveStream($artistId: ID!) {
    artist(id: $artistId) {
      id
      currentLive {
        id
        isPremium
      }
    }
  }
`
